import { useEffect, useState } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import Checkbox from '../../ui/elements/checkbox'
import Select from '../../ui/elements/select'

type ColumnMapping = {
  displayName: string
  columnNames: string[]
}

type ColumnMappings = Record<string, ColumnMapping>

export type FileMapping = {
  mappedColumns: (string | undefined)[]
  mappedCsv: Record<string, string>[]
}

type CsvImporterProps = {
  columnMappings: ColumnMappings
  file: File
  onChange?: (mapping: FileMapping) => void
}

export default function CsvImporter({
  columnMappings,
  file,
  onChange,
}: CsvImporterProps) {
  const { t } = useLingui()
  const [mapping, setMapping] = useState<(string | undefined)[]>([])
  const [data, setData] = useState<string[][]>([])
  const [hasHeaders, setHasHeaders] = useState<boolean>(true)

  useEffect(() => {
    readData()

    async function readData() {
      const data = await parseCsv(file)
      setData(data)
      setHasHeaders(data[0].every(column => isNaN(column as any)))
      setMapping(guessMappings(columnMappings, data))
    }
  }, [file, columnMappings])
  useEffect(() => {
    if (mapping.length && onChange)
      onChange({
        mappedColumns: mapping,
        mappedCsv: data.slice(hasHeaders ? 1 : 0).map(row =>
          mapping.reduce((mappedRow, columnMapping, i) => {
            if (columnMapping) {
              mappedRow[columnMapping] = row[i]
            }
            return mappedRow
          }, {} as Record<string, string>)
        ),
      })
  }, [onChange, mapping, data, hasHeaders])

  const headers = data[0]

  return data && data.length > 0 ? (
    <>
      <div className="tw-w-full tw-border tw-border-system-grey-100 tw-border-solid tw-rounded-sm">
        <table className="tw-text-xs tw-w-full">
          <thead>
            <tr>
              {headers.map((header, i) => {
                const colMapping = mapping[i]

                return (
                  <th key={header} className="tw-px-2 tw-py-3">
                    {hasHeaders && <div className="tw-ml-2">{header}</div>}
                    <div>
                      <Select
                        value={colMapping || ''}
                        onChange={e => updateMapping(i, e.target.value)}>
                        <option value="">{t`Not used`}</option>
                        {Object.keys(columnMappings).map(attribute => {
                          const { displayName } = columnMappings[attribute]
                          return (
                            <option key={attribute} value={attribute}>
                              {t`Use as ${displayName}`}
                            </option>
                          )
                        })}
                      </Select>
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {data.slice(hasHeaders ? 1 : 0, 5).map((row, i) => (
              <tr
                key={i}
                className="tw-border tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-system-grey-100">
                {row.map((col, i) => (
                  <td key={i} className="tw-pl-4 tw-pr-2 tw-py-3">
                    {col}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="tw-my-4">
        <Checkbox
          active={hasHeaders}
          onChange={e => setHasHeaders(e.target.checked)}>
          <Trans>Treat first line as headers</Trans>
        </Checkbox>
      </div>
    </>
  ) : null

  function updateMapping(index: number, attribute: string) {
    const nextMapping = mapping.map(x => (x !== attribute ? x : undefined))
    nextMapping[index] = attribute || undefined
    setMapping(nextMapping)
  }
}

export async function parseCsv(file: File) {
  const contents = new TextDecoder().decode(await file.arrayBuffer())
  const lines = contents
    .split('\n')
    .map(line => line.trim())
    .filter((line, i) => i === 0 || !line.startsWith('#'))

  const separator =
    lines[0] && lines[0].indexOf('\t') >= 0
      ? '\t'
      : lines[0].indexOf(',') >= 0
      ? ','
      : ';'

  return lines.map(line =>
    line
      .split(separator)
      // This is a very naïve handling of quotes and apostrophes, a lot of
      // CSVs will not be correctly handled.
      .map(col => col.trim().replace(/"/g, '').replace(/'/g, ''))
  )
}

export function guessMappings(
  columnMappings: ColumnMappings,
  csv: string[][]
): (string | undefined)[] {
  const headings = csv[0]
  if (headings[0].startsWith('#')) {
    headings[0] = headings[0].substring(1).trim()
  }

  const columnNames = Object.keys(columnMappings)
  const guessedMappings = []

  for (const heading of headings) {
    const normalizedHeading = heading.trim().toLowerCase()

    const matchIndex = columnNames.findIndex(c =>
      columnMappings[c].columnNames.includes(normalizedHeading)
    )

    if (matchIndex >= 0) {
      guessedMappings.push(columnNames[matchIndex])
      columnNames.splice(matchIndex, 1)
    } else {
      guessedMappings.push(undefined)
    }
  }
  return guessedMappings
}
